import React from 'react';
import { Link } from '../routing';
import { useTranslation } from 'react-i18next';
import TraderIdentity from './TraderIdentity';
import theme from '../theme';

/**
 * The footer that carries who we are and where the legal texts live.
 *
 * Art. 5 of the E-Commerce Directive wants the trader's identity "easily,
 * directly and permanently accessible", and CRD Art. 6(1) wants the same
 * information available before the consumer is bound. A link from every page
 * satisfies both — but it has to be from EVERY page, and it was not: the trader
 * identity and the Terms/Privacy/Contact links appeared on the landing page and
 * on the three legal pages themselves, and nowhere else. Browse, the product
 * page, checkout, My Rentals and the whole signed-in half of the site had no
 * footer at all, which is to say the customer was closest to being bound exactly
 * where the information was hardest to find.
 *
 * Three near-identical copies of this markup also existed, each reading its
 * labels from a different namespace (`terms.privacyPolicy`,
 * `privacy.privacyPolicy`, `contact.privacyPolicy` — same words, three keys, and
 * three places to forget). They all render this now, from `footer.*`.
 *
 * `variant` picks the ground it sits on: 'dark' for pages that end in ink,
 * 'light' for the cream ones. Nothing else differs.
 */
export default function LegalFooter({ variant = 'dark', style }) {
  const { t } = useTranslation();
  const dark = variant !== 'light';

  const bg = dark ? theme.inkDeep : theme.creamBone;
  const fg = dark ? theme.alpha.white(0.4) : theme.grey500;
  // The identity is quieter than the links: it is reference material, not
  // navigation, and it is long once a real legal name and address are in it.
  const idFg = dark ? theme.alpha.white(0.3) : theme.grey400;

  const link = { color: fg, textDecoration: 'none', margin: '0 14px', whiteSpace: 'nowrap' };

  return (
    <footer style={{ background: bg, padding: '40px 24px', color: fg, fontSize: 13, textAlign: 'center', ...style }}>
      <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', rowGap: 10 }}>
        <Link to="/privacy" style={link}>{t('footer.privacyPolicy')}</Link>
        <Link to="/terms" style={link}>{t('footer.termsOfService')}</Link>
        <Link to="/withdrawal-form" style={link}>{t('footer.withdrawalForm')}</Link>
        <Link to="/contact" style={link}>{t('footer.contact')}</Link>
      </div>

      {/* Renders nothing at all while the company identity is unconfigured —
          null fields rather than the word PLACEHOLDER on every page of a live
          shop. Filling COMPANY_* in the backend .env makes it appear here and in
          the legal pages at once, with no rebuild. */}
      <TraderIdentity style={{ fontSize: 11, lineHeight: 1.9, color: idFg, margin: '0 0 14px' }} />

      <div>{t('footer.copyright', { year: new Date().getFullYear() })}</div>
    </footer>
  );
}
